import { Link, useNavigate } from "react-router-dom";
import { Cloud, LogOut, Sparkles, Languages, Coins } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";

const CURRENCIES = [
  { code: "eur", label: "EUR €" },
  { code: "usd", label: "USD $" },
  { code: "gbp", label: "GBP £" },
  { code: "chf", label: "CHF" },
  { code: "cad", label: "CAD" },
];

export default function Navbar({ onOpenAuth }) {
  const { user, logout } = useAuth();
  const { locale, setLocale, currency, setCurrency, t } = useT();
  const nav = useNavigate();

  return (
    <header className="sticky top-0 z-50 border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" data-testid="navbar-brand-logo" className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 shadow-lg shadow-cyan-500/30">
            <Cloud className="h-5 w-5 text-slate-950" strokeWidth={2.5} />
          </div>
          <span className="text-lg font-bold tracking-tight">
            <span className="text-gradient">Nexus</span>
            <span className="text-slate-100">Cloud</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 text-sm text-slate-300 md:flex">
          <a href="/#pricing" data-testid="nav-pricing-link" className="hover:text-cyan-300">{t("nav_pricing")}</a>
          <a href="/#security" className="hover:text-cyan-300">{t("nav_security")}</a>
          <a href="/#faq" className="hover:text-cyan-300">{t("nav_faq")}</a>
          <a href="/#support" data-testid="nav-support-link" className="hover:text-cyan-300">{t("nav_support")}</a>
        </nav>

        <div className="flex items-center gap-2">
          <select
            data-testid="currency-select"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="hidden rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs uppercase text-slate-200 hover:bg-slate-800 sm:inline-block"
            aria-label="Currency"
          >
            {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
          <button
            data-testid="lang-switch-btn"
            onClick={() => setLocale(locale === "it" ? "en" : "it")}
            className="flex items-center gap-1 rounded-md border border-slate-700 bg-transparent px-2 py-1 text-xs uppercase text-slate-200 hover:bg-slate-800"
            aria-label="Cambia lingua / Change language"
          >
            <Languages className="h-3.5 w-3.5" />
            {locale === "it" ? "IT" : "EN"}
          </button>
          {user ? (
            <>
              <Button
                variant="ghost"
                data-testid="nav-dashboard-btn"
                className="text-slate-200 hover:text-cyan-300"
                onClick={() => nav("/dashboard")}
              >
                {t("nav_dashboard")}
              </Button>
              <Button
                variant="outline"
                data-testid="nav-logout-btn"
                className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800"
                onClick={async () => { await logout(); nav("/"); }}
              >
                <LogOut className="mr-2 h-4 w-4" /> {t("nav_logout")}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                data-testid="nav-redeem-btn"
                className="hidden text-slate-200 hover:text-cyan-300 sm:inline-flex"
                onClick={() => nav("/redeem")}
              >
                <Sparkles className="mr-2 h-4 w-4" /> {t("nav_redeem")}
              </Button>
              <Button
                variant="outline"
                data-testid="nav-login-btn"
                className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800"
                onClick={() => onOpenAuth?.("login")}
              >
                {t("nav_login")}
              </Button>
              <Button
                data-testid="nav-signup-btn"
                className="bg-cyan-400 text-slate-950 hover:bg-cyan-300"
                onClick={() => onOpenAuth?.("register")}
              >
                {t("nav_signup")}
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
